import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Heart, 
  Check, Sparkles, Globe, Landmark, Camera, ShieldCheck
} from 'lucide-react';
import styles from './Creators.module.scss';
import clsx from 'clsx';

// Images (using the ones generated)
const IMG_WORKSPACE = '/assets/images/panama_creator_professional_workspace_1778199372293.png';
const IMG_LOCATION = '/assets/images/panama_creator_on_location_luxury_1778199387443.png';
const IMG_ACCESS = '/assets/images/panama_creator_institutional_access_1778199403514.png';

// ==============================
// MINI MOCKUPS (REAL UI)
// ==============================
const MiniCreatorFeed = ({ t }) => (
  <div className={styles.uiMockup}>
    <div className={styles.mockBrowser}>
      <div className={styles.dots}><span></span><span></span><span></span></div>
      <div className={styles.url}>{t('mockups.feed.url')}</div>
    </div>
    <div className={styles.mockBody}>
        <div className={styles.mockFeedHeader}>{t('mockups.feed.header')}</div>
        <div className={styles.mockFeedScroll}>
            <div className={styles.feedCard}>
                <div className={styles.feedBrand}>{t('mockups.feed.card1.brand')}</div>
                <div className={styles.feedTitle}>{t('mockups.feed.card1.title')}</div>
                <div className={styles.feedBrief}>{t('mockups.feed.card1.brief')}</div>
                <div className={styles.feedTags}>
                    <span>{t('mockups.feed.card1.tag')}</span>
                    <span className={styles.verify}>{t('mockups.feed.card1.verify')}</span>
                </div>
            </div>
            <div className={styles.feedCard}>
                <div className={styles.feedBrand}>{t('mockups.feed.card2.brand')}</div>
                <div className={styles.feedTitle}>{t('mockups.feed.card2.title')}</div>
                <div className={styles.feedBrief}>{t('mockups.feed.card2.brief')}</div>
                <div className={styles.feedTags}>
                    <span>{t('mockups.feed.card2.tag')}</span>
                </div>
            </div>
        </div>
    </div>
  </div>
);

const MiniEarnings = ({ t }) => (
  <div className={styles.earningsUi}>
    <div className={styles.earHeader}>{t('mockups.earnings.header')}</div>
    <div className={styles.earBalance}>
        <span className={styles.val}>$2,840.00</span>
        <span className={styles.lab}>{t('mockups.earnings.balance_label')}</span>
    </div>
    <div className={styles.earHistory}>
        {t('mockups.earnings.history', { returnObjects: true }).map((item, i) => (
          <div key={i} className={styles.histItem}>
              <span className={styles.histBrand}>{item.brand}</span>
              <span className={styles.histAmt}>{item.amount}</span>
          </div>
        ))}
    </div>
    <div className={styles.earBtn}>{t('mockups.earnings.btn')}</div>
  </div>
);

const Creators = () => {
  const { t } = useTranslation('creators_landing');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const slideUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.23, 1, 0.32, 1] } }
  };

  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } }
  };

  return (
    <div className={styles.creatorsPage}>
      {/* 1. HERO */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <motion.div initial="hidden" animate="visible" variants={stagger} className={styles.heroInner}>
            <motion.div variants={slideUp} className={styles.sectionEyebrow}>
              {t('hero.eyebrow')}
            </motion.div>
            <motion.h1 variants={slideUp} className={styles.heroTitle}>
              {t('hero.title')}
            </motion.h1>
            <motion.p variants={slideUp} className={styles.heroSubtitle}>
              {t('hero.subtitle')}
            </motion.p>
            <motion.div variants={slideUp} className={styles.ctaBox}>
              <Link to="/register" className={styles.primaryBtn}>
                {t('hero.primary')}
              </Link>
              <Link to="/login" className={styles.secondaryBtn}>
                {t('hero.secondary')}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 2. STATS / BENEFITS */}
      <section className={styles.statsSection}>
        <div className={styles.container}>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}><ShieldCheck size={24} /></div>
              <div className={styles.statInfo}>
                <span className={styles.label}>{t('stats.settlement.label')}</span>
                <span className={styles.value}>{t('stats.settlement.value')}</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}><Sparkles size={24} /></div>
              <div className={styles.statInfo}>
                <span className={styles.label}>{t('stats.narrative.label')}</span>
                <span className={styles.value}>{t('stats.narrative.value')}</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}><Heart size={24} /></div>
              <div className={styles.statInfo}>
                <span className={styles.label}>{t('stats.affinity.label')}</span>
                <span className={styles.value}>{t('stats.affinity.value')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. PAIN/SOLUTION */}
      <section className={styles.featuresSection}>
        <div className={styles.container}>
          <div className={styles.featureBlock}>
            <div className={styles.featureText}>
              <div className={styles.sectionEyebrow}>{t('features.briefing.eyebrow')}</div>
              <h3>{t('features.briefing.title')}</h3>
              <p>{t('features.briefing.desc')}</p>
              <ul className={styles.checkList}>
                {t('features.briefing.benefits', { returnObjects: true }).map((benefit, i) => (
                  <li key={i}><Check size={16} /> {benefit}</li>
                ))}
              </ul>
            </div>
            <div className={styles.featureVisual}>
              <MiniCreatorFeed t={t} />
            </div>
          </div>

          <div className={clsx(styles.featureBlock, styles.reversed)}>
             <div className={styles.featureVisual}>
              <MiniEarnings t={t} />
            </div>
            <div className={styles.featureText}>
              <div className={styles.sectionEyebrow}>{t('features.sovereignty.eyebrow')}</div>
              <h3>{t('features.sovereignty.title')}</h3>
              <p>{t('features.sovereignty.desc')}</p>
              <ul className={styles.checkList}>
                {t('features.sovereignty.benefits', { returnObjects: true }).map((benefit, i) => (
                  <li key={i}><Check size={16} /> {benefit}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: THE PANAMA NARRATIVE */}
      <section className={styles.ecosystemSection}>
        <div className={styles.container}>
          <div className={styles.ecosystemHeader}>
            <div className={styles.sectionEyebrow}>{t('latam_ecosystem.eyebrow')}</div>
            <h2 className={styles.sectionTitle}>{t('latam_ecosystem.title')}</h2>
            <p className={styles.darkSectionLead}>
              {t('latam_ecosystem.desc')}
            </p>
          </div>
          
          <div className={styles.ecosystemGrid}>
             <div className={styles.ecosystemItems}>
                <div className={styles.ecosystemItem}>
                   <div className={styles.ecosystemIcon}>
                      <Camera size={28} />
                   </div>
                   <div>
                      <h4>{t('latam_ecosystem.items.curation.title')}</h4>
                      <p>{t('latam_ecosystem.items.curation.desc')}</p>
                   </div>
                </div>
                <div className={styles.ecosystemItem}>
                   <div className={styles.ecosystemIcon}>
                      <Landmark size={28} />
                   </div>
                   <div>
                      <h4>{t('latam_ecosystem.items.access.title')}</h4>
                      <p>{t('latam_ecosystem.items.access.desc')}</p>
                   </div>
                </div>
                <div className={styles.ecosystemItem}>
                   <div className={styles.ecosystemIcon}>
                      <Globe size={28} />
                   </div>
                   <div>
                      <h4>{t('latam_ecosystem.items.standards.title')}</h4>
                      <p>{t('latam_ecosystem.items.standards.desc')}</p>
                   </div>
                </div>
             </div>
             <div className={styles.ecosystemMedia}>
                <img src={IMG_WORKSPACE} alt="Professional Workspace" />
                <div className={styles.ecosystemOverlay}>
                   <span>{t('latam_ecosystem.innovation.badge')}</span>
                   <h3>{t('latam_ecosystem.innovation.title')}</h3>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* NEW: CREATOR ON-LOCATION GALLERY */}
      <section className={styles.creatorGallerySection}>
        <div className={styles.container}>
           <div className={styles.creatorGalleryGrid}>
              <div className={styles.creatorGalleryTile}>
                 <img src={IMG_LOCATION} alt="On Location" />
                 <div>
                    <h4>{t('gallery.residency')}</h4>
                 </div>
              </div>
              <div className={styles.creatorGalleryTile}>
                 <img src={IMG_ACCESS} alt="Institutional Access" />
                 <div>
                    <h4>{t('gallery.partnership')}</h4>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* 4. FINAL CTA */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaInner}>
            <div className={styles.sectionEyebrow}>{t('cta_final.eyebrow')}</div>
            <h2>{t('cta_final.title')}</h2>
            <p>{t('cta_final.desc')}</p>
            <div className={styles.ctaActions}>
              <Link to="/register" className={styles.ctaPrimary}>{t('cta_final.primary')}</Link>
              <Link to="/about" className={styles.ctaSecondary}>{t('cta_final.secondary')}</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Creators;
